import { z } from 'zod'
import { counted } from './counted'

/**
 * Who owns what a large list does with rows nobody is working on: inactive accounts and paused
 * reports. They are hidden by default, one pill away, and `any` shows everything with active rows
 * first. Every list reads its `status` search param through these, so `?status=any` means the same
 * on every page and a page's other filters never have to know about it.
 */
export type HiddenStatus = 'paused' | 'inactive'
export type StatusFilter<H extends HiddenStatus> = 'active' | H | 'any'

/** Optional, because a link to the list should not have to name the default. */
export const reportStatusFilterSchema = z
  .enum(['active', 'paused', 'any'])
  .optional()
  .catch(undefined)
export const accountStatusFilterSchema = z
  .enum(['active', 'inactive', 'any'])
  .optional()
  .catch(undefined)

export const DEFAULT_STATUS_FILTER = 'active'

export interface StatusCounts {
  active: number
  hidden: number
  all: number
}

export function splitByStatus<T extends { status: string }, H extends HiddenStatus>(
  rows: T[],
  filter: StatusFilter<H>,
): { shown: T[]; counts: StatusCounts } {
  const active = rows.filter((row) => row.status === 'active')
  const hidden = rows.filter((row) => row.status !== 'active')
  const counts = { active: active.length, hidden: hidden.length, all: rows.length }
  if (filter === 'active') return { shown: active, counts }
  if (filter === 'any') return { shown: [...active, ...hidden], counts }
  return { shown: hidden, counts }
}

export function statusPillOptions<H extends HiddenStatus>(
  hidden: H,
  counts: StatusCounts,
): Array<{ value: StatusFilter<H>; label: string }> {
  return [
    { value: 'active', label: `Active (${counts.active})` },
    {
      value: hidden,
      label: `${hidden === 'paused' ? 'Paused' : 'Inactive'} (${counts.hidden})`,
    },
    { value: 'any', label: `All (${counts.all})` },
  ]
}

/**
 * Why a list is empty, when the filter is the reason. Null means the filter has nothing to say: a
 * search found nothing in a list with rows, or nothing exists yet.
 */
export function hiddenRowsHint<H extends HiddenStatus>(
  filter: StatusFilter<H>,
  counts: StatusCounts,
  noun: 'report' | 'account',
  hidden: H,
): string | null {
  if (filter === 'any') return null
  if (filter === hidden)
    return counts.hidden === 0 && counts.active > 0 ? `No ${hidden} ${noun}s.` : null
  if (counts.hidden === 0) return null
  if (counts.active === 0) {
    return counts.hidden === 1
      ? `The one ${noun} is ${hidden}.`
      : `All ${counted(counts.hidden, noun)} are ${hidden}.`
  }
  // Only a search empties a list that has active rows. No count: the page's rows are counted before
  // the search, so a number here would read as how many of the hidden rows match it.
  return `No active ${noun}s match. ${hidden === 'paused' ? 'Paused' : 'Inactive'} ${noun}s aren’t shown.`
}

/** Whether a list's emptiness is the filter's doing, not "nothing exists yet". */
export function statusFilterNarrows<H extends HiddenStatus>(
  filter: StatusFilter<H>,
  counts: StatusCounts,
): boolean {
  return filter !== 'any' && counts.all > 0
}

/** A short list with nothing to hide gets no pills, unless someone has moved off the default. */
export function showsStatusPills<H extends HiddenStatus>(
  filter: StatusFilter<H>,
  counts: StatusCounts,
): boolean {
  return counts.hidden > 0 || filter !== DEFAULT_STATUS_FILTER
}

/**
 * The accounts the workspace switcher offers Clipbook staff: the live ones, plus the one they are
 * in, which it has to be able to name. A customer's list is theirs and does not change.
 */
export function switcherAccounts<T extends { id: string; status: string }>(
  accounts: T[],
  currentAccountId: string | undefined,
  staff: boolean,
): T[] {
  if (!staff) return accounts
  return accounts.filter(
    (account) => account.status === 'active' || account.id === currentAccountId,
  )
}
